import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import envConfig from '../../../../envConfig';
import {
  createNotificationGroup,
  editNotificationGroup,
  selectNotificationGroupsCrudOperation,
  selectNotificationGroupsCrudStatus,
} from '../../../../redux/slices/notificationGroupsSlice/notificationGroupsSlice';

import { CrudType } from '../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import useNotificationGroupUpdater from '../useNotificationGroupUpdater';
import { RaaRoles } from '../../../../utility/SecurityRules';
import NotificationGroupFormInner from './NotificationGroupFormInner';
import useNotificationGroupForm from './useNotificationGroupForm';

function NotificationGroupForm(props) {
  const dispatch = useDispatch();

  const crudOperation = useSelector(selectNotificationGroupsCrudOperation);
  const crudStatus = useSelector(selectNotificationGroupsCrudStatus);

  const {
    handleCreateNotificationGroupSuccess,
    handleCreateNotificationGroupFailure,
    handleUpdateNotificationGroupSuccess,
    handleUpdateNotificationGroupFailure,
    isFormSubmitting,
    setIsFormSubmitting,
    submitBtnText,
    setSubmitBtnText,
  } = useNotificationGroupUpdater();

  React.useEffect(() => {
    if (crudOperation === CrudType.CREATE) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleCreateNotificationGroupSuccess();
          break;
        case CrudStatus.ERROR:
          handleCreateNotificationGroupFailure();
          break;
        default:
          break;
      }
    }
    if (crudOperation === CrudType.UPDATE) {
      switch (crudStatus) {
        case CrudStatus.SUCCESS:
          handleUpdateNotificationGroupSuccess();
          break;
        case CrudStatus.ERROR:
          handleUpdateNotificationGroupFailure();
          break;
        default:
          break;
      }
    }
  }, [crudOperation, crudStatus]);

  const allowedRoles = [
    RaaRoles.ADMIN.id,
    RaaRoles.MANAGER.id,
    RaaRoles.VIEWER.id,
  ];

  const { initNotificationGroup, users } = props;

  // Pre-filter recipients based on user validity before initializing form state
  const userIds = users.users.filter((user) => allowedRoles.includes(parseInt(user.roleId, 10)))
    .map((user) => user.userId);

  const filteredInitGroup = {
    ...initNotificationGroup,
    recipients: initNotificationGroup.recipients.filter(
      (recipient) => !recipient.userId || userIds.includes(recipient.userId),
    ),
  };

  const {
    notificationGroup,
    errorList,
    submittable,
    validateRecipients,
    handleRecipientChange,
    addRecipientRow,
    onDeleteBtnPress,
    onNameChange,
    mapNotificationGroupForSave,
  } = useNotificationGroupForm(filteredInitGroup);

  const save = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setSubmitBtnText('Saving...');
    const areRecipientsValid = validateRecipients(notificationGroup.recipients);
    if (!areRecipientsValid.includes(false)) {
      await dispatch(createNotificationGroup(mapNotificationGroupForSave()));
    } else {
      setIsFormSubmitting(false);
      setSubmitBtnText('Save Changes');
    }
  };

  const edit = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setSubmitBtnText('Saving...');
    const areRecipientsValid = validateRecipients(notificationGroup.recipients);
    if (!areRecipientsValid.includes(false)) {
      const newNotificationGroup = mapNotificationGroupForSave();
      await dispatch(editNotificationGroup({
        notificationGroup: newNotificationGroup,
        patchFields: ['name', 'recipients'],
      }));
    } else {
      setIsFormSubmitting(false);
      setSubmitBtnText('Save Changes');
    }
  };

  return (
    <div className="pb-3" style={{ maxWidth: '1100px' }}>
      <h2 className="mb-3">Notification Group Form</h2>
      <NotificationGroupFormInner
        users={users}
        notificationGroup={notificationGroup}
        errorList={errorList}
        onNameChange={onNameChange}
        handleRecipientChange={handleRecipientChange}
        onDeleteBtnPress={onDeleteBtnPress}
        addRecipientRow={addRecipientRow}
        isFormDisabled={false}
      />
      <div className="mt-5">
        <button type="button" className="btn btn-lg btn-primary" disabled={isFormSubmitting || !submittable} onClick={notificationGroup.id ? edit : save}>
          {submitBtnText}
        </button>
        &nbsp;
        <button type="button" className="btn btn-link text-muted" onClick={() => window.location.assign(`${envConfig.raaUrl}/s/actions/notification-groups`)}>Cancel</button>
      </div>
    </div>
  );
}

NotificationGroupForm.defaultProps = {
  users: null,
};

NotificationGroupForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  initNotificationGroup: PropTypes.object.isRequired,
  users: PropTypes.shape({
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        emailAddress: PropTypes.string,
        roleId: PropTypes.string,
      }),
    ),
  }),
};

export default NotificationGroupForm;
